import { SmartlistType } from "../../api/MediaAPI.types";
import { smartlistEditPath } from "../../utils/smartlist";
import { CONTENT_TYPE } from "../content/Type";
import { castingPath, contentPath, displayPath } from "../routes/routes";

// SelectBox items are heterogeneous and only partially typed upstream: declare only what we read here.
export interface SelectBoxItemPathInput {
  itemType?: string;
  type?: string;
  uuid?: string;
  content_uuid?: string;
}

interface SelectBoxItemPathParams {
  item: SelectBoxItemPathInput;
  projectSlug?: string;
  seasonToSerieMap?: Map<string, string>;
}

const SMARTLIST_TYPE_VALUE_LIST: readonly string[] =
  Object.values(SmartlistType);

function isSmartlistType(value: string): value is SmartlistType {
  return SMARTLIST_TYPE_VALUE_LIST.includes(value);
}

function contentItemPath(
  uuid: string,
  type: string | undefined,
): string | undefined {
  if (type === CONTENT_TYPE.AD_BREAK) return undefined;

  return contentPath(type, uuid);
}

function seasonItemPath(
  uuid: string,
  projectSlug: string | undefined,
  seasonToSerieMap: Map<string, string> | undefined,
): string | undefined {
  // A season has no standalone edit route: link to its parent series instead.
  const serieUuid = seasonToSerieMap?.get(uuid);

  if (!serieUuid) return undefined;

  return smartlistEditPath(projectSlug, SmartlistType.SERIES, serieUuid);
}

function smartlistItemPath(
  uuid: string,
  type: string | undefined,
  projectSlug: string | undefined,
  seasonToSerieMap: Map<string, string> | undefined,
): string | undefined {
  if (type === SmartlistType.SEASON) {
    return seasonItemPath(uuid, projectSlug, seasonToSerieMap);
  }

  if (type && isSmartlistType(type)) {
    return smartlistEditPath(projectSlug, type, uuid);
  }

  return undefined;
}

// Editor route for a SelectBox item, or undefined when it has no navigable page.
export function selectBoxItemPath({
  item,
  projectSlug,
  seasonToSerieMap,
}: SelectBoxItemPathParams): string | undefined {
  // Item is still loading (rendered as a Spinner by SelectBoxItem): no page to link to yet.
  if (item.content_uuid || !item.uuid) return undefined;

  switch (item.itemType) {
    case "video":
    case "audio":
      return contentItemPath(item.uuid, item.type);
    case "casting":
      return castingPath(item.uuid);
    case "display":
      return displayPath(projectSlug, item.uuid);
    case "smartlist":
      return smartlistItemPath(
        item.uuid,
        item.type,
        projectSlug,
        seasonToSerieMap,
      );
    default:
      return undefined;
  }
}
